import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Badge,
  InputBase,
  Paper,
  Tabs,
  Tab,
  Avatar,
  Card,
  CardActionArea,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const ApprovalDashboard = ({
  requests,
  onSelectRequest,
  onBackToApprovals,
  notificationCount = 3,
}) => {
  const [activeTab, setActiveTab] = useState("Pending");
  const [searchQuery, setSearchQuery] = useState("");

  // Counts
  const pendingCount = requests.filter((r) => r.status === "Pending").length;
  const approvedCount = requests.filter((r) => r.status === "Approved").length;
  const rejectedCount = requests.filter((r) => r.status === "Rejected").length;

  // Filter requests
  const filteredRequests = requests.filter((req) => {
    const matchesTab = req.status === activeTab;
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch =
      query === "" ||
      req.name.toLowerCase().includes(query) ||
      req.employeeNo.toLowerCase().includes(query) ||
      req.country.toLowerCase().includes(query);

    return matchesTab && matchesSearch;
  });

  const getStatusChip = (status) => {
    if (status === "Pending") {
      return (
        <Chip
          label="Pending"
          size="small"
          sx={{
            backgroundColor: "#FEF3C7",
            color: "#D97706",
            fontWeight: 600,
            fontSize: "0.75rem",
            height: "24px",
            borderRadius: "6px",
            border: "1px solid #FDE68A",
          }}
        />
      );
    }
    if (status === "Approved") {
      return (
        <Chip
          label="Approved"
          size="small"
          sx={{
            backgroundColor: "#DCFCE7",
            color: "#16A34A",
            fontWeight: 600,
            fontSize: "0.75rem",
            height: "24px",
            borderRadius: "6px",
            border: "1px solid #BBF7D0",
          }}
        />
      );
    }
    return (
      <Chip
        label="Rejected"
        size="small"
        sx={{
          backgroundColor: "#FEE2E2",
          color: "#DC2626",
          fontWeight: 600,
          fontSize: "0.75rem",
          height: "24px",
          borderRadius: "6px",
          border: "1px solid #FECACA",
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "540px",
        margin: "0 auto",
        minHeight: "100vh",
        backgroundColor: "#F8FAFC",
        pb: 6,
      }}
    >
      {/* ===== STICKY HEADER + TABS BLOCK ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#F8FAFC",
        }}
      >
        {/* Top Blue Header */}
        <Box
          sx={{
            background: "linear-gradient(180deg, #0056C6 0%, #004AAD 100%)",
            color: "#ffffff",
            px: 2.5,
            pt: 1.5,
            pb: 3,
            borderBottomLeftRadius: 24,
            borderBottomRightRadius: 24,
            boxShadow: "0 6px 20px rgba(0, 74, 173, 0.22)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={onBackToApprovals}
                sx={{
                  color: "#ffffff",
                  p: 0.8,
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 24 }} />
              </IconButton>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    letterSpacing: "-0.01em",
                    lineHeight: 1.2,
                  }}
                >
                  Foreign Travel Approval
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    color: "rgba(255, 255, 255, 0.85)",
                    mt: 0.3,
                  }}
                >
                  Review and approve travel requests
                </Typography>
              </Box>
            </Box>

            <IconButton
              sx={{
                color: "#ffffff",
                p: 1,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
              }}
            >
              <Badge
                badgeContent={notificationCount}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: "#EF4444",
                    color: "#ffffff",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    height: 18,
                    minWidth: 18,
                  },
                }}
              >
                <NotificationsIcon sx={{ fontSize: 24 }} />
              </Badge>
            </IconButton>
          </Box>

          {/* Search Bar inside Header */}
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 0.7,
              borderRadius: "30px",
              backgroundColor: "#ffffff",
              boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
            }}
          >
            <SearchIcon sx={{ color: "#94A3B8", fontSize: 20, mr: 1 }} />
            <InputBase
              fullWidth
              placeholder="Search by name or employee no..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                fontSize: "0.88rem",
                color: "#1E293B",
                "& input::placeholder": {
                  color: "#94A3B8",
                  opacity: 1,
                },
              }}
            />
            {searchQuery && (
              <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0.5 }}>
                <ClearIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
              </IconButton>
            )}
          </Paper>
        </Box>

        {/* Filter Tabs */}
        <Box sx={{ px: 2, pt: 1.5, backgroundColor: "#F8FAFC" }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          variant="fullWidth"
          sx={{
            borderBottom: "1px solid #E2E8F0",
            minHeight: "44px",
            "& .MuiTabs-indicator": {
              backgroundColor: "#0052CC",
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab
            value="Pending"
            label={`Pending (${pendingCount})`}
            sx={{
              textTransform: "none",
              fontWeight: activeTab === "Pending" ? 700 : 500,
              fontSize: "0.92rem",
              color: activeTab === "Pending" ? "#0052CC" : "#64748B",
              minHeight: "44px",
              py: 1,
              "&.Mui-selected": { color: "#0052CC" },
            }}
          />
          <Tab
            value="Approved"
            label={`Approved (${approvedCount})`}
            sx={{
              textTransform: "none",
              fontWeight: activeTab === "Approved" ? 700 : 500,
              fontSize: "0.92rem",
              color: activeTab === "Approved" ? "#0052CC" : "#64748B",
              minHeight: "44px",
              py: 1,
              "&.Mui-selected": { color: "#0052CC" },
            }}
          />
          <Tab
            value="Rejected"
            label={`Rejected (${rejectedCount})`}
            sx={{
              textTransform: "none",
              fontWeight: activeTab === "Rejected" ? 700 : 500,
              fontSize: "0.92rem",
              color: activeTab === "Rejected" ? "#0052CC" : "#64748B",
              minHeight: "44px",
              py: 1,
              "&.Mui-selected": { color: "#0052CC" },
            }}
          />
        </Tabs>
      </Box>
      </Box>

      {/* Requests List */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 3,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {filteredRequests.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 2,
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              border: "1px dashed #CBD5E1",
              mt: 1,
            }}
          >
            <FlightTakeoffIcon sx={{ fontSize: 48, color: "#CBD5E1", mb: 1.5 }} />
            <Typography sx={{ fontWeight: 600, color: "#334155", fontSize: "1rem" }}>
              No {activeTab.toLowerCase()} travel requests
            </Typography>
            <Typography sx={{ color: "#94A3B8", fontSize: "0.85rem", mt: 0.5 }}>
              {searchQuery
                ? `No results matching "${searchQuery}"`
                : `There are currently no requests in the ${activeTab.toLowerCase()} category.`}
            </Typography>
          </Box>
        ) : (
          filteredRequests.map((req) => (
            <Card
              key={req.id}
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: "1px solid #E2E8F0",
                backgroundColor: "#ffffff",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  borderColor: "#CBD5E1",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <CardActionArea
                onClick={() => onSelectRequest(req)}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {/* Left side: Avatar + Details */}
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flex: 1 }}>
                  <Avatar
                    src={req.avatar}
                    alt={req.name}
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: "50%",
                      mt: 0.5,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  />

                  <Box sx={{ flex: 1 }}>
                    {/* Name */}
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.98rem",
                        color: "#1E293B",
                        lineHeight: 1.3,
                      }}
                    >
                      {req.name}
                    </Typography>

                    {/* Employee No */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        color: "#0052CC",
                        mt: 0.4,
                      }}
                    >
                      <BadgeOutlinedIcon sx={{ fontSize: 16 }} />
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#0052CC" }}>
                        {req.employeeNo}
                      </Typography>
                    </Box>

                    {/* Destination Country */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                        color: "#334155",
                        mt: 0.5,
                      }}
                    >
                      <FlightTakeoffIcon sx={{ fontSize: 16, color: "#64748B" }} />
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#334155" }}>
                        {req.country}
                      </Typography>
                    </Box>

                    {/* Date Range */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                        color: "#64748B",
                        mt: 0.4,
                      }}
                    >
                      <CalendarTodayOutlinedIcon sx={{ fontSize: 15, color: "#94A3B8" }} />
                      <Typography sx={{ fontSize: "0.8rem", color: "#64748B" }}>
                        {req.dateRange}
                      </Typography>
                    </Box>

                    {/* Status Pill */}
                    <Box sx={{ mt: 1 }}>{getStatusChip(req.status)}</Box>
                  </Box>
                </Box>

                {/* Right Arrow */}
                <ChevronRightIcon sx={{ color: "#94A3B8", fontSize: 24, ml: 1 }} />
              </CardActionArea>
            </Card>
          ))
        )}
      </Box>
    </Box>
  );
};

export default ApprovalDashboard;
